// ─── Binding Resolver ─────────────────────────────────────────────────────────
//
// Phase 6d Runtime Layer. Sole consumer of Phase 6b (componentRegistry) and
// Phase 6c (componentBindings) registries. Owns the three-API rendering
// boundary:
//
//   getResolvedPropsForComponent  - read (rendering layer)
//   subscribeToComponent          - reactive (rendering layer)
//   dispatchComponentEvent        - event dispatch (interaction layer)
//
// ─── Computed Binding Expression Authoring Rules ─────────────────────────────
//
// dependsOn entries are inserted directly into deps without aliasing,
// normalization, or translation. The key in deps IS the key from dependsOn.
//
// Scoped state keys (containing ':') REQUIRE bracket notation in expressions:
//
//   dependsOn: ["global:count"]
//     Valid:    deps["global:count"]
//     Invalid:  deps.count            ← accesses deps["count"] = undefined → null
//
//   dependsOn: ["page:home:filter"]
//     Valid:    deps["page:home:filter"]
//
//   dependsOn: ["component:btn1:active"]
//     Valid:    deps["component:btn1:active"]
//
// Dot notation is valid ONLY for identifier-compatible keys (alphanumeric, _, $):
//
//   dependsOn: ["myDerived"]
//     Valid:    deps.myDerived
//     Valid:    deps["myDerived"]
//
// Import path: @/components/bindingResolver

import { compileExpression, ExpressionCompileError } from '@/core/expressionCompiler';
import {
  getAllBindings,
  getBindingsForComponent,
  getAllEventBindings,
} from '@/components/componentBindings';
import type { RegisteredEventBinding } from '@/components/componentBindings';
import { appState } from '@/state/appStateStore';
import { queryCollection } from '@/data/collectionEngine';
import { actionExecutor } from '@/actions/actionExecutor';
import { eventBus } from '@/events/eventBus';
import { logger } from '@/system/logger';
import type { BindingDefinition } from '@/core/schema/bindingSchema';
import type { StateSchemaMap, CollectionSchema } from '@/core/schema/appSchema';
import type { StateValue } from '@/appEngine/stateEngine';
import type { NuvraEvent } from '@/events/eventBus';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResolvedComponentProps {
  props:      Record<string, StateValue>;
  styles:     Record<string, StateValue>;
  visibility: boolean;
  enabled:    boolean;
  texts:      Record<string, StateValue>;
}

export type BindingChangeListener = (
  bindingId: string,
  newValue: StateValue,
  previousValue: StateValue,
) => void;

export type ComponentBindingChangeListener = (
  componentId: string,
  props: ResolvedComponentProps,
) => void;

// ─── Error ────────────────────────────────────────────────────────────────────

export class BindingResolverError extends Error {
  constructor(
    public readonly bindingId: string,
    public readonly cause: unknown,
  ) {
    super(
      `[BindingResolverError] binding "${bindingId}": ${
        cause instanceof Error ? cause.message : String(cause)
      }`
    );
    this.name = 'BindingResolverError';
  }
}

// ─── Resolver ─────────────────────────────────────────────────────────────────

class BindingResolverImpl {
  private _resolvedValues      = new Map<string, StateValue>();
  private _compiledExpressions = new Map<string, (deps: Record<string, StateValue>) => StateValue>();
  private _bindingIndex        = new Map<string, BindingDefinition>();
  private _stateKeyIndex       = new Map<string, Set<string>>();
  private _collectionIndex     = new Map<string, Set<string>>();
  private _eventIndex          = new Map<string, RegisteredEventBinding[]>();
  private _bindingSubscribers  = new Map<string, Set<BindingChangeListener>>();
  private _componentSubscribers = new Map<string, Set<ComponentBindingChangeListener>>();
  private _projectId: string | null = null;
  private _stateUnsub: (() => void) | null = null;
  private _eventUnsub: (() => void) | null = null;

  init(projectId: string, _stateMap: StateSchemaMap, _dataMap: CollectionSchema[]): void {
    if (this._projectId !== null) {
      logger.error('[bindingResolver] already initialized - ignoring duplicate init call');
      return;
    }

    // ─── Step 2 - Store projectId ─────────────────────────────────────────────
    this._projectId = projectId;

    // ─── Step 3 - Build indexes, compile computed expressions ─────────────────
    for (const { binding } of getAllBindings()) {
      this._bindingIndex.set(binding.id, binding);

      const source = binding.source;

      if (source.type === 'state') {
        this._addToIndex(this._stateKeyIndex, source.stateKey, binding.id);
      } else if (source.type === 'computed') {
        for (const key of source.dependsOn) {
          this._addToIndex(this._stateKeyIndex, key, binding.id);
        }
        try {
          const fn = compileExpression(source.expression);
          this._compiledExpressions.set(binding.id, fn);
        } catch (e) {
          if (e instanceof ExpressionCompileError) {
            throw new BindingResolverError(binding.id, e);
          }
          throw e;
        }
      } else if (source.type === 'data_query') {
        this._addToIndex(this._collectionIndex, source.collection, binding.id);
      }
      // static - no index entry needed
    }

    // ─── Step 4 - Build event index ───────────────────────────────────────────
    for (const eventBinding of getAllEventBindings()) {
      const key = `${eventBinding.event.componentId}:${eventBinding.event.eventName}`;
      const existing = this._eventIndex.get(key);
      if (existing) {
        existing.push(eventBinding);
      } else {
        this._eventIndex.set(key, [eventBinding]);
      }
    }

    // ─── Step 5 - Initial evaluation ─────────────────────────────────────────
    for (const { binding } of getAllBindings()) {
      const value = this._evaluateBinding(binding);
      this._resolvedValues.set(binding.id, value);
    }

    // ─── Step 6 - Subscribe to state changes ─────────────────────────────────
    this._stateUnsub = appState.subscribeAll(
      (key: string, value: StateValue, previousValue: StateValue) =>
        this._onStateChanged(key, value, previousValue),
    );

    // ─── Step 7 - Subscribe to all events ────────────────────────────────────
    this._eventUnsub = eventBus.onAny((event: NuvraEvent) => this._onEvent(event));
  }

  clear(): void {
    this._stateUnsub?.();
    this._stateUnsub = null;

    this._eventUnsub?.();
    this._eventUnsub = null;

    this._resolvedValues.clear();
    this._compiledExpressions.clear();
    this._bindingIndex.clear();
    this._stateKeyIndex.clear();
    this._collectionIndex.clear();
    this._eventIndex.clear();
    this._bindingSubscribers.clear();
    this._componentSubscribers.clear();

    this._projectId = null;
  }

  getResolvedProp(bindingId: string): StateValue {
    return this._resolvedValues.get(bindingId) ?? null;
  }

  getResolvedPropsForComponent(componentId: string): ResolvedComponentProps {
    const result: ResolvedComponentProps = {
      props:      {},
      styles:     {},
      visibility: true,
      enabled:    true,
      texts:      {},
    };

    for (const { binding } of getBindingsForComponent(componentId)) {
      const value = this._resolvedValues.get(binding.id) ?? null;
      switch (binding.targetType) {
        case 'prop':
          result.props[binding.targetKey] = value;
          break;
        case 'style':
          result.styles[binding.targetKey] = value;
          break;
        case 'visibility':
          result.visibility = !!value;
          break;
        case 'enabled':
          result.enabled = !!value;
          break;
        case 'text':
          result.texts[binding.targetKey] = value;
          break;
      }
    }

    return result;
  }

  subscribeToBinding(bindingId: string, listener: BindingChangeListener): () => void {
    if (!this._bindingSubscribers.has(bindingId)) {
      this._bindingSubscribers.set(bindingId, new Set());
    }
    this._bindingSubscribers.get(bindingId)!.add(listener);
    return () => this._bindingSubscribers.get(bindingId)?.delete(listener);
  }

  subscribeToComponent(componentId: string, listener: ComponentBindingChangeListener): () => void {
    if (!this._componentSubscribers.has(componentId)) {
      this._componentSubscribers.set(componentId, new Set());
    }
    this._componentSubscribers.get(componentId)!.add(listener);
    return () => this._componentSubscribers.get(componentId)?.delete(listener);
  }

  async dispatchComponentEvent(componentId: string, eventName: string): Promise<void> {
    const key = `${componentId}:${eventName}`;
    const bindings = this._eventIndex.get(key) ?? [];
    if (bindings.length === 0) {
      logger.warn(`[bindingResolver] no event bindings for "${componentId}:${eventName}"`);
      return;
    }
    for (const eventBinding of bindings) {
      await actionExecutor.execute(eventBinding.event.actionId, this._projectId!);
    }
  }

  // ─── Internals ───────────────────────────────────────────────────────────────

  private _addToIndex(index: Map<string, Set<string>>, key: string, bindingId: string): void {
    if (!index.has(key)) index.set(key, new Set());
    index.get(key)!.add(bindingId);
  }

  private _evaluateBinding(binding: BindingDefinition): StateValue {
    const source = binding.source;

    switch (source.type) {
      case 'state': {
        const value = appState.get(source.stateKey);
        if (source.transform && source.transform.trim() !== '') {
          const transformFn = compileExpression(source.transform);
          return transformFn({ value });
        }
        return value;
      }

      case 'computed': {
        const fn = this._compiledExpressions.get(binding.id)!;
        const deps: Record<string, StateValue> = {};
        for (const key of source.dependsOn) {
          deps[key] = appState.get(key);
        }
        return fn(deps);
      }

      case 'data_query': {
        let records = queryCollection(source.collection);

        if (source.filter && source.filter.trim() !== '') {
          const filterFn = compileExpression(source.filter);
          records = records.filter(record => {
            const deps = { ...record } as Record<string, StateValue>;
            return !!filterFn(deps);
          });
        }

        if (source.orderBy && source.orderBy.trim() !== '') {
          const field = source.orderBy;
          records = records.slice().sort((a, b) => {
            const av = a[field];
            const bv = b[field];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return av < bv ? -1 : 1;
          });
        }

        if (source.limit !== undefined && source.limit > 0) {
          records = records.slice(0, source.limit);
        }

        return records as unknown as StateValue;
      }

      case 'static':
        return source.value as StateValue;
    }
  }

  private _onStateChanged(key: string, _value: StateValue, _previousValue: StateValue): void {
    const bindingIds = this._stateKeyIndex.get(key);
    if (!bindingIds || bindingIds.size === 0) return;

    for (const id of bindingIds) {
      const binding = this._bindingIndex.get(id);
      if (!binding) continue;

      const previous = this._resolvedValues.get(id) ?? null;
      let newValue: StateValue;

      try {
        newValue = this._evaluateBinding(binding);
      } catch (e) {
        logger.error(`[bindingResolver] evaluation error for binding "${id}"`, { error: e });
        continue;
      }

      this._resolvedValues.set(id, newValue);
      if (newValue !== previous) {
        this._notifyChange(id, newValue, previous);
      }
    }
  }

  private _onEvent(event: NuvraEvent): void {
    if (
      event.type !== 'app:record_inserted' &&
      event.type !== 'app:record_updated' &&
      event.type !== 'app:record_deleted'
    ) return;

    const collection = event.collection;
    const bindingIds = this._collectionIndex.get(collection);
    if (!bindingIds || bindingIds.size === 0) return;

    for (const id of bindingIds) {
      const binding = this._bindingIndex.get(id);
      if (!binding) continue;

      const previous = this._resolvedValues.get(id) ?? null;
      let newValue: StateValue;

      try {
        newValue = this._evaluateBinding(binding);
      } catch (e) {
        logger.error(`[bindingResolver] evaluation error for binding "${id}"`, { error: e });
        continue;
      }

      this._resolvedValues.set(id, newValue);
      if (newValue !== previous) {
        this._notifyChange(id, newValue, previous);
      }
    }
  }

  private _notifyChange(
    bindingId: string,
    newValue: StateValue,
    previousValue: StateValue,
  ): void {
    const bindingListeners = this._bindingSubscribers.get(bindingId);
    if (bindingListeners) {
      for (const fn of bindingListeners) {
        try {
          fn(bindingId, newValue, previousValue);
        } catch (e) {
          logger.error(
            `[bindingResolver] binding subscriber error for "${bindingId}"`,
            { error: e },
          );
        }
      }
    }

    const componentId = this._bindingIndex.get(bindingId)?.componentId;
    if (!componentId) return;

    const componentListeners = this._componentSubscribers.get(componentId);
    if (!componentListeners || componentListeners.size === 0) return;

    const resolvedProps = this.getResolvedPropsForComponent(componentId);
    for (const fn of componentListeners) {
      try {
        fn(componentId, resolvedProps);
      } catch (e) {
        logger.error(
          `[bindingResolver] component subscriber error for "${componentId}"`,
          { error: e },
        );
      }
    }
  }
}

export const bindingResolver = new BindingResolverImpl();

export const initBindingResolver          = bindingResolver.init.bind(bindingResolver);
export const clearBindingResolver         = bindingResolver.clear.bind(bindingResolver);
export const getResolvedProp              = bindingResolver.getResolvedProp.bind(bindingResolver);
export const getResolvedPropsForComponent = bindingResolver.getResolvedPropsForComponent.bind(bindingResolver);
export const subscribeToBinding           = bindingResolver.subscribeToBinding.bind(bindingResolver);
export const subscribeToComponent         = bindingResolver.subscribeToComponent.bind(bindingResolver);
export const dispatchComponentEvent       = bindingResolver.dispatchComponentEvent.bind(bindingResolver);
